import { classifyPick, documentProblem } from '@/onboarding/pickContent';
import {
  ACCEPT,
  MAX_CREATE_PHOTOS,
  MAX_TEXT_CHARS,
  MAX_TEXT_FILES,
  MAX_TEXT_TOTAL,
  isPhoto,
  readPicks,
  type PickDeps,
  type Picked,
} from '../project/attachments';
import type { DraftDocument, DraftMedia } from '../../state/draft';

/**
 * What the create step does with each file picked, apart from the screen that shows it.
 *
 * Deep-dive F3 (2026-09-30): the step took one file per trip through the picker, stored it under
 * its raw name and in its raw format, and a later file of the same name replaced it. A .heic from
 * a Mac was served as image/heic — a broken hero on Chrome and Android; a 12-megapixel original
 * made the page 20-40MB; a raw name like "Theo's ring.jpg" failed the server's name rule, so every
 * later chat edit was never told the photo existed; and a .tiff passed here and was refused by the
 * upload AFTER the build had been paid for. Photos now go through chat's pipeline (readPicks: a
 * JPEG no wider than 2000px under a safe, unique name), and a sound or clip is judged against the
 * upload's own allowlist and size limit at pick time.
 *
 * Deep-dive F8 and owner decision D1: a .txt or .md of up to 20,000 characters is PAGE CONTENT by
 * default (a menu with its prices, a schedule), up to 3 of them and 40,000 characters together —
 * the /edit limits, so a file does the same thing here as in chat. A longer one, or one past those
 * limits, is "a long text people read": the attributed chapter reader (S.asset), one per app.
 */

/** For the file picker: chat's photos and text, and the sounds and clips the upload takes. */
export const CREATE_ACCEPT = [
  ACCEPT,
  'audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/wav', '.mp3', '.m4a', '.aac', '.wav',
  'video/mp4', 'video/quicktime', 'video/webm', '.mp4', '.mov', '.webm',
].join(',');

/** The upload's own limit (infra/lambda/handlers/upload.ts MAX_UPLOAD_BYTES). */
export const MAX_CLIP_BYTES = 100 * 1024 * 1024;
/** The build prompt lists 20 files by name; past that the model is never told about one. */
export const MAX_CREATE_MEDIA = 20;
/** D1's thresholds — the /edit contract's, so a file behaves the same in both places. */
export const PAGE_DOC_CHARS = MAX_TEXT_CHARS;
export const PAGE_DOCS = MAX_TEXT_FILES;
export const PAGE_DOCS_TOTAL = MAX_TEXT_TOTAL;

/**
 * The audio and video types upload.ts accepts, each with the extension it stores. Photos are not
 * here: every photo is re-encoded as a JPEG before it gets near the upload.
 */
const CLIP_TYPES: Record<string, string> = {
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/aac': 'aac',
  'audio/wav': 'wav',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

/*
 * What browsers and Macs call those same types. A Mac hands over a voice memo as audio/x-m4a,
 * which the upload would refuse — but it IS audio/mp4, so it is declared as what it is.
 */
const CLIP_ALIASES: Record<string, string> = {
  'audio/x-m4a': 'audio/mp4',
  'audio/m4a': 'audio/mp4',
  'audio/mp3': 'audio/mpeg',
  'audio/x-mp3': 'audio/mpeg',
  'audio/x-aac': 'audio/aac',
  'audio/x-wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'audio/vnd.wave': 'audio/wav',
};

const CLIP_BY_EXTENSION: Record<string, string> = Object.fromEntries(
  Object.entries(CLIP_TYPES).map(([type, ext]) => [ext, type]),
);

/** The upload type for a sound or clip, or null when the upload would refuse it. */
function clipType(file: File): string | null {
  const given = (file.type || '').trim().toLowerCase();
  const named = CLIP_ALIASES[given] ?? given;
  if (CLIP_TYPES[named]) return named;
  if (given && given !== 'application/octet-stream') return null;
  // No type given, as some pickers do: judged by the extension, as the phone does.
  const ext = (file.name.toLowerCase().split('.').pop() ?? '');
  return CLIP_BY_EXTENSION[ext === 'mpeg' ? 'mp3' : ext] ?? null;
}

/**
 * A name the page can address a sound or clip by — the photo rule (`photoName`), keeping the
 * clip's own kind of extension: word characters, dots and dashes, unique, at most 120 characters.
 */
function clipName(fileName: string, ext: string, used: Set<string>): string {
  const base = fileName.replace(/\.[^.]*$/, '').replace(/[^\w.-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '').slice(0, 100)
    || 'clip';
  let name = `${base}.${ext}`;
  for (let n = 2; used.has(name.toLowerCase()); n += 1) name = `${base}-${n}.${ext}`;
  used.add(name.toLowerCase());
  return name;
}

export type DocUse = 'page' | 'reader';
export interface CreateDoc extends DraftDocument {
  use: DocUse;
}
export interface CreatePicked {
  media: DraftMedia[];
  docs: CreateDoc[];
}

const count = (n: number) => n.toLocaleString('en-US');

/**
 * Why `doc` cannot be page content beside `others` (the OTHER page documents), or null when it
 * can. The sentence is shown beside the choice, so a disabled option is never unexplained.
 */
export function pageContentProblem(doc: DraftDocument, others: DraftDocument[]): string | null {
  if (doc.text.length > PAGE_DOC_CHARS) {
    return `It has ${count(doc.text.length)} characters, and a page can be written from up to ${count(PAGE_DOC_CHARS)}.`;
  }
  if (others.length >= PAGE_DOCS) return `A page can be written from up to ${PAGE_DOCS} files.`;
  const total = others.reduce((sum, d) => sum + d.text.length, 0) + doc.text.length;
  if (total > PAGE_DOCS_TOTAL) {
    return `Together with the others it would be ${count(total)} characters, and a page can be written from up to ${count(PAGE_DOCS_TOTAL)}.`;
  }
  return null;
}

/** A text file as UTF-8 — `Blob.text()` where there is one, a FileReader where not (jsdom). */
function readText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
    reader.readAsText(file, 'utf-8');
  });
}

/** What one pick brought: only the new things, and a sentence for each that could not be used. */
export interface CreateRead {
  /** Named against the lists the pick was read against; `mergeCreatePicks` re-checks them. */
  media: DraftMedia[];
  /** Read and found usable as text. Whether each is page content is decided at the merge. */
  docs: DraftDocument[];
  problems: string[];
}

/**
 * Read what was picked — the slow half: text read, photos re-encoded — and answer with ONLY what
 * it brought. `current` is the lists as they were when the pick started, used to name things and
 * to stop converting photos there is no room for; the lists themselves are never handed back.
 *
 * Review finding (2026-09-30): this answered with the WHOLE new lists, which the step wrote over
 * the draft after the await. A photo's line from the describe call, a caption typed, a photo or a
 * document removed while a pick was being read (a HEIC takes seconds on Safari) were all undone —
 * the removed item came back and the line was gone for good. `mergeCreatePicks` puts what this
 * returns into the lists as they are once it has finished.
 */
export async function readCreatePicks(
  files: File[],
  current: CreatePicked,
  deps: PickDeps = {},
): Promise<CreateRead> {
  const problems: string[] = [];
  // Every name in use, the new ones included, so no two new things are named alike.
  const media = [...current.media];
  const added: DraftMedia[] = [];
  const docs: DraftDocument[] = [];
  const used = new Set(media.map((m) => m.name.toLowerCase()));

  const photoFiles: File[] = [];
  for (const file of files) {
    if (isPhoto(file)) {
      photoFiles.push(file);
      continue;
    }
    const verdict = classifyPick(file.name, file.type);
    if (verdict.kind === 'document') {
      let body: string;
      try {
        body = await readText(file);
      } catch {
        problems.push(`Could not read ${file.name}. Try adding it again.`);
        continue;
      }
      const refusal = documentProblem(body, file.size);
      if (refusal) {
        problems.push(`${file.name}: ${refusal}`);
        continue;
      }
      const name = file.name.slice(0, 120);
      // The same file twice in one pick: the later copy is the one meant.
      const at = docs.findIndex((d) => d.name === name);
      if (at >= 0) docs.splice(at, 1);
      docs.push({ name, text: body });
      continue;
    }
    const type = clipType(file);
    if (!type) {
      problems.push(
        verdict.kind === 'unsupported'
          ? `${file.name} can’t be added — ${verdict.reason}`
          : `${file.name} can’t be added — use a JPEG, PNG or HEIC photo, an MP3, M4A or WAV sound, or an MP4, MOV or WebM video.`,
      );
      continue;
    }
    if (file.size > MAX_CLIP_BYTES) {
      problems.push(`${file.name} is over 100 MB, which is more than a site can hold in one file. Trim it and add it again.`);
      continue;
    }
    if (media.length >= MAX_CREATE_MEDIA) {
      problems.push(`Up to ${MAX_CREATE_MEDIA} files can be added, so ${file.name} was left out.`);
      continue;
    }
    const clip: DraftMedia = { name: clipName(file.name, CLIP_TYPES[type], used), contentType: type, file };
    media.push(clip);
    added.push(clip);
  }

  if (photoFiles.length) {
    const picked: Picked[] = media
      .filter((m) => m.contentType === 'image/jpeg')
      .map((m) => ({ kind: 'photo', key: m.name, name: m.name, original: m.name, file: m.file }));
    const room = Math.min(MAX_CREATE_PHOTOS, picked.length + (MAX_CREATE_MEDIA - media.length));
    const read = await readPicks(
      photoFiles,
      // Clips' names too, so no photo can take one; they keep their own extension, but a name is a name.
      { picked, existing: media.filter((m) => m.contentType !== 'image/jpeg').map((m) => m.name), maxPhotos: room },
      deps,
    );
    problems.push(...read.problems);
    for (const p of read.added) {
      if (p.kind === 'photo') added.push({ name: p.name, contentType: 'image/jpeg', file: p.file });
    }
  }

  return { media: added, docs, problems };
}

/** `name` with `-2`, `-3`… before its extension until nothing in `used` has it. */
function uniqueName(name: string, used: Set<string>): string {
  if (!used.has(name.toLowerCase())) return name;
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  let n = 2;
  while (used.has(`${base}-${n}${ext}`.toLowerCase())) n += 1;
  return `${base}-${n}${ext}`;
}

/**
 * Put what a pick read into the lists as they are NOW — after the await, not before it — and
 * answer with the whole new lists, what was actually added, and a sentence for each thing that
 * no longer fits. Synchronous, so nothing can change between reading the draft and writing it.
 *
 * Everything already in `now` stays exactly as it is: its line, its caption, and its absence when
 * it was removed. Names and limits are checked again against `now`, since another pick may have
 * landed while this one was read. Nothing refused here is ever sent: a limit met after the build
 * was paid for is a charge for a refusal.
 */
export function mergeCreatePicks(
  now: CreatePicked,
  read: Pick<CreateRead, 'media' | 'docs'>,
): CreatePicked & { added: DraftMedia[]; problems: string[] } {
  const problems: string[] = [];
  const media = [...now.media];
  const added: DraftMedia[] = [];
  const used = new Set(media.map((m) => m.name.toLowerCase()));
  let photos = media.filter((m) => m.contentType === 'image/jpeg').length;

  for (const m of read.media) {
    const photo = m.contentType === 'image/jpeg';
    if (photo && photos >= MAX_CREATE_PHOTOS) {
      problems.push(`Up to ${MAX_CREATE_PHOTOS} photos can be added, so ${m.file.name} was left out.`);
      continue;
    }
    if (media.length >= MAX_CREATE_MEDIA) {
      problems.push(`Up to ${MAX_CREATE_MEDIA} files can be added, so ${m.file.name} was left out.`);
      continue;
    }
    const name = uniqueName(m.name, used);
    used.add(name.toLowerCase());
    // A photo's file carries its name (readPicks), so a renamed photo is a renamed file.
    const entry: DraftMedia = name === m.name
      ? m
      : { ...m, name, file: m.file.name === m.name ? new File([m.file], name, { type: m.file.type, lastModified: m.file.lastModified }) : m.file };
    media.push(entry);
    added.push(entry);
    if (photo) photos += 1;
  }

  const docs = [...now.docs];
  for (const doc of read.docs) {
    // The same file again is the newer copy of it, not a second document — decided against the
    // OTHERS, so a copy that cannot be used leaves the old one where it was (review nit,
    // 2026-09-30: it was spliced out first, and a refused copy took the menu with it).
    const at = docs.findIndex((d) => d.name === doc.name);
    const others = docs.filter((d) => d.name !== doc.name);
    const use: DocUse = pageContentProblem(doc, others.filter((d) => d.use === 'page')) ? 'reader' : 'page';
    const reader = others.find((d) => d.use === 'reader');
    if (use === 'reader' && reader) {
      problems.push(`${doc.name} is long, and one long text can be read in an app — remove ${reader.name} first to use it instead.`);
      continue;
    }
    if (at >= 0) docs.splice(at, 1, { ...doc, use });
    else docs.push({ ...doc, use });
  }

  return { media, docs, added, problems };
}
